/**
 * Shared behavior for the input-driven cells (TEXT / NUMBER / ZIP / DATE).
 *
 * - `hydrateFromValue` turns the parent's `string | null` into the input's
 *   visible string. The fix-errors backend uses `-` as a "missing" sentinel,
 *   which renders blank in the input itself but as a placeholder via the
 *   surrounding cell.
 * - `commitInputChange` is the onBlur transition: empty re-stamps the cell as
 *   missing (or clears optional fields), otherwise compares against the
 *   original value to decide whether the row is now error-free.
 */

import type { CellChangeData } from './types';

export const hydrateFromValue = (value: string | null): string => {
  if (value === null || value === '-') return '';
  return value;
};

interface CommitInputChangeArgs {
  inputValue: string;
  originalValue: string | null;
  id: number;
  name: string;
  isActive: boolean;
  errorMessage: string | undefined;
  isOptionalField: boolean;
  /** Date cells normalize ISO → MM/DD/YYYY before emitting; pass that mapping in. */
  normalizeOutput?: (value: string) => string;
  /** Date cells compare against the slash-converted original value. */
  resolveCompareValue?: (originalValue: string | null) => string | null;
  setData: (data: CellChangeData) => void;
}

export const commitInputChange = ({
  inputValue,
  originalValue,
  id,
  name,
  isActive,
  errorMessage,
  isOptionalField,
  normalizeOutput,
  resolveCompareValue,
  setData,
}: CommitInputChangeArgs): void => {
  if (inputValue === '') {
    if (!isOptionalField) {
      setData({ value: '-', id, name, isActive: true, errorMessage });
    } else {
      setData({ value: undefined, id, name, isActive: false, errorMessage });
    }
    return;
  }

  const normalised = normalizeOutput ? normalizeOutput(inputValue) : inputValue;
  const compareValue = resolveCompareValue
    ? resolveCompareValue(originalValue)
    : originalValue;

  const matchesOriginal =
    JSON.stringify(compareValue) === JSON.stringify(inputValue) ||
    JSON.stringify(originalValue) === JSON.stringify(inputValue);

  setData({
    value: normalised,
    id,
    name,
    isActive: matchesOriginal ? isActive === true : false,
    errorMessage,
  });
};
